/**
 * GMotif — the shyrdak-inspired diamond background motif (STORY-000-006a).
 *
 * The Central Asian shyrdak felt-rug motif is the only cultural cue
 * in the GardEn visual language (see docs/GardEn/DESIGN-SYSTEM.md
 * "Cultural & ethical notes"). Used as a subtle decorative background
 * on hero bands and FAQ blocks. Always purely decorative — never
 * load-bearing for meaning, never selectable.
 *
 * Implementation: inline `<svg>` JSX literal with `<defs><pattern>` so
 * the diamond tile repeats via SVG-native pattern fills. Stroke uses
 * `currentColor`, so the wrapper `<div>` sets text-color (via a
 * Tailwind utility) and the SVG picks it up. This avoids the
 * data-URL-stuffed-via-innerHTML pattern the guardrails forbid
 * (STORY-000-006a guardrails note + REQ-PRD-§11.2-C).
 *
 * Visual reference: docs/GardEn/src/system.jsx `MOTIF_SVG()` and
 * docs/GardEn/src/ui.jsx `GMotif`.
 */
import { useId, type ReactElement } from "react";

/** Visual tone for the motif strokes. Each tone resolves to the
 * matching `--g-*` token via the corresponding Tailwind text utility. */
export type GMotifTone = "primary" | "accent" | "ink";

export interface GMotifProps {
  /** Stroke tone for the diamond pattern. Defaults to `primary`. */
  tone?: GMotifTone;
  /** Per-instance opacity for the motif background. Defaults to 0.5,
   * matching the prototype's `<GMotif>` default. The CSS variable
   * `--g-motif-op` (declared in theme.css, defaults to 1) is the
   * global multiplier reserved for a future Tweaks-panel-style
   * toggle; this prop layers on top. */
  opacity?: number;
  /** Additional classes (e.g. positioning + sizing utilities). The
   * component is `aria-hidden` and renders no children, so it is
   * typically positioned absolutely behind a content layer. */
  className?: string;
}

const toneToTextClass: Record<GMotifTone, string> = {
  primary: "text-primary",
  accent: "text-accent",
  ink: "text-foreground",
};

/**
 * Renders an SVG-tiled diamond motif as a decorative background.
 *
 * The component is `aria-hidden="true"` and adds no semantic content;
 * pair it with a sibling positioned content layer.
 */
export function GMotif({
  tone = "primary",
  opacity = 0.5,
  className,
}: GMotifProps): ReactElement {
  // SVG `<pattern>` IDs must be unique per page; `useId()` gives us a
  // stable per-instance identifier across SSR and hydration (React 19).
  const reactId = useId();
  const patternId = `garden-motif-${reactId.replace(/:/g, "")}`;
  const tonalClass = toneToTextClass[tone];
  const classes = ["pointer-events-none", tonalClass, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      aria-hidden="true"
      data-testid="garden-motif"
      data-tone={tone}
      className={classes}
      style={{ opacity }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        focusable="false"
        role="presentation"
      >
        <defs>
          <pattern
            id={patternId}
            x="0"
            y="0"
            width="80"
            height="80"
            patternUnits="userSpaceOnUse"
          >
            <g
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              opacity="0.5"
            >
              <path d="M40 4 L76 40 L40 76 L4 40 Z" />
              <path d="M40 16 L64 40 L40 64 L16 40 Z" />
              <path d="M40 28 L52 40 L40 52 L28 40 Z" />
            </g>
          </pattern>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill={`url(#${patternId})`}
        />
      </svg>
    </div>
  );
}
